import { BalanceData } from "../data/balance";
import { Command, CommandResult, TimedCommand, commandToString } from "./command";
import { SimConstants } from "./constants";
import { LevelPhase, LevelSim } from "./levelSim";
import { GearBonus } from "../meta/gear";
import { LevelModifiers, NO_MODIFIERS } from "./modifiers";

export interface ReplayResult {
  readonly hash: bigint;
  readonly hashHex: string;
  readonly phase: LevelPhase;
  readonly ticks: number;
  readonly stagesCleared: number;
  readonly kills: number;
  readonly leaks: number;
}

export class ReplayError extends Error {}

/** Re-runs a recorded command log headlessly and returns the end-state hash. */
export function runReplay(balance: BalanceData, level: number, seed: bigint, owned: readonly boolean[], commands: readonly TimedCommand[], maxTicks = SimConstants.MaxTicksPerLevel, towerLevel: number | readonly number[] = 1, firstClear = false, gear: readonly GearBonus[] = [], modifiers: LevelModifiers = NO_MODIFIERS): ReplayResult {
  const sim = new LevelSim(balance, level, seed, owned, towerLevel, firstClear, gear, modifiers);
  for (const tc of commands) {
    if (tc.tick < sim.tick) throw new ReplayError(`Command ${commandToString(tc.command)} at tick ${tc.tick} is out of order (sim at ${sim.tick})`);
    while (sim.tick < tc.tick) {
      if (sim.isOver) throw new ReplayError(`Level ended before command ${commandToString(tc.command)} at tick ${tc.tick}`);
      sim.step();
    }
    const r = sim.execute(tc.command);
    if (r !== CommandResult.Ok) throw new ReplayError(`Command ${commandToString(tc.command)} at tick ${tc.tick} failed: ${CommandResult[r]}`);
  }
  while (!sim.isOver && sim.tick < maxTicks) sim.step();
  const hash = sim.computeHash();
  return { hash, hashHex: hash.toString(16).padStart(16, "0"), phase: sim.phase, ticks: sim.tick, stagesCleared: sim.stagesCleared, kills: sim.kills, leaks: sim.leaks };
}

export type { Command };
